import { address as toAddress } from "@solana/kit";
import type { Address } from "@solana/kit";
import { LAMPORTS_PER_SOL } from "@/config/constants";

/**
 * Shortens a Solana address for display.
 *
 * Phase 6 slice 4: addresses are now Kit `Address` (base58 string) — no
 * `.toBase58()` necessary. The legacy `PublicKey` overload is dropped; pass
 * the string directly.
 */
export function shortenAddress(address: string, chars: number = 4): string {
  if (address.length <= chars * 2) {
    return address;
  }
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}

/**
 * Formats lamports to SOL with proper decimal places
 * @param lamports - Amount in lamports
 * @param decimals - Number of decimal places to show (default: 4)
 * @returns Formatted SOL amount string
 */
export function formatSOL(
  lamports: number | bigint,
  decimals: number = 4,
): string {
  const sol = Number(lamports) / LAMPORTS_PER_SOL;
  return sol.toFixed(decimals);
}

/**
 * Formats lamports to SOL with compact notation for large amounts
 * @param lamports - Amount in lamports
 * @returns Formatted string like "1.23K SOL" or "45.6M SOL"
 */
export function formatSOLCompact(lamports: number | bigint): string {
  const sol = Number(lamports) / LAMPORTS_PER_SOL;

  if (sol >= 1_000_000) {
    return `${(sol / 1_000_000).toFixed(2)}M SOL`;
  }
  if (sol >= 1_000) {
    return `${(sol / 1_000).toFixed(2)}K SOL`;
  }
  return `${sol.toFixed(4)} SOL`;
}

/**
 * Formats token amount with proper decimals
 * @param amount - Raw token amount
 * @param decimals - Token decimals
 * @param displayDecimals - Number of decimal places to display (default: 2)
 * @returns Formatted token amount string
 */
export function formatToken(
  amount: number | bigint,
  decimals: number,
  displayDecimals: number = 2,
): string {
  const value = Number(amount) / Math.pow(10, decimals);
  return value.toFixed(displayDecimals);
}

/**
 * Formats token amount with symbol
 * @param amount - Raw token amount
 * @param decimals - Token decimals
 * @param symbol - Token symbol
 * @param displayDecimals - Number of decimal places to display (default: 2)
 * @returns Formatted string like "123.45 USDC"
 */
export function formatTokenWithSymbol(
  amount: number | bigint,
  decimals: number,
  symbol: string,
  displayDecimals: number = 2,
): string {
  const formatted = formatToken(amount, decimals, displayDecimals);
  return `${formatted} ${symbol}`;
}

/**
 * Converts SOL to lamports
 * @param sol - Amount in SOL
 * @returns Amount in lamports
 */
export function solToLamports(sol: number): number {
  return Math.floor(sol * LAMPORTS_PER_SOL);
}

/**
 * Converts lamports to SOL
 * @param lamports - Amount in lamports
 * @returns Amount in SOL
 */
export function lamportsToSol(lamports: number | bigint): number {
  return Number(lamports) / LAMPORTS_PER_SOL;
}

/**
 * Formats USD value with proper notation
 * @param value - USD value
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted USD string like "$1,234.56"
 */
export function formatUSD(value: number, decimals: number = 2): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Formats a number with thousands separators
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string like "1,234.56"
 */
export function formatNumber(value: number, decimals: number = 2): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Validates if a string is a valid base58 Solana address.
 *
 * Note: drops the legacy "is on curve" check — Kit's `address()` brand
 * validator only checks base58 format/length. PDAs (off-curve) are valid
 * addresses for our purposes (they're written and read all the time).
 */
export function isValidSolanaAddress(address: string): boolean {
  try {
    toAddress(address);
    return true;
  } catch {
    return false;
  }
}

/**
 * Gets Solana Explorer URL for an address.
 */
export function getExplorerAddressUrl(
  address: string | Address,
  network: string = "mainnet-beta",
): string {
  const cluster = network === "mainnet-beta" ? "" : `?cluster=${network}`;
  return `https://explorer.solana.com/address/${address}${cluster}`;
}

/**
 * Gets Solana Explorer URL for a transaction
 * @param signature - Transaction signature
 * @param network - Network name (default: 'mainnet-beta')
 * @returns Explorer URL
 */
export function getExplorerTransactionUrl(
  signature: string,
  network: string = "mainnet-beta",
): string {
  const cluster = network === "mainnet-beta" ? "" : `?cluster=${network}`;
  return `https://explorer.solana.com/tx/${signature}${cluster}`;
}

/**
 * Formats a timestamp to human-readable date
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Formatted date string
 */
export function formatDate(timestamp: number): string {
  // `timeZoneName: "short"` appends e.g. "GMT+8" / "PST" so the user can't
  // misread a wall-clock time against the wrong timezone. Anchored here so
  // every caller (notably the create-market confirm dialog before signing)
  // surfaces an unambiguous absolute time.
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(timestamp));
}

/**
 * Returns the user's local IANA timezone name and short offset, e.g.
 * `{ name: "Asia/Shanghai", offset: "GMT+8" }`. Used to render explicit
 * "times below are in your local timezone" hints next to datetime-local
 * inputs and admin displays so users can sanity-check before signing.
 */
export function getLocalTimezone(): { name: string; offset: string } {
  const name = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZoneName: "short",
  }).formatToParts(new Date());
  const offset = parts.find((p) => p.type === "timeZoneName")?.value ?? "UTC";
  return { name, offset };
}

/**
 * Formats a timestamp to relative time (e.g., "2 hours ago")
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Relative time string
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }
  if (hours > 0) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  return "Just now";
}
